// PostMoreMenu.tsx — ··· (more) menu for a single PostCard.
//
// Two variants, driven by `isOwn` (post.authorId === current user's id):
//   Own post    → Edit post / (Publish now / Cancel schedule) / (Add to thread) /
//                  Copy link (1.24.00) / (Why am I seeing this?) /
//                  (View analytics) / Delete post.
//   Other's post → Follow/Unfollow, Copy link, Not interested, — divider —,
//                  Mute/Unmute, Report post.
//
// TruXSocial is mute-only by design — there is no Block feature, and none is planned.
//
// Follow/Mute/Copy-link are self-contained here (hooks + services); Edit/Delete/Report
// are host callbacks because they open dialogs that live at the PostCard/HeroPostCard level.

import { useState } from 'react';
import styled from 'styled-components';
import { Popover, IconButton } from '@/ui';
import { useToast } from '@/ui';
import { Icon } from '@/icons';
import type { IconName } from '@/icons';
import type { Post } from '@/core/models/post';
import type { ForYouReason } from '@/core/models/post';
import { useFollow, useMute, useRequireAuth, useSession } from '@/hooks';
import { useWriteGate } from '@/hooks/writeGate';
import { postsService } from '@/core/services/postsService';
import { buildPostShareUrl } from '@/core/lib/postUrl';
import { startThreadPart } from '@/core/stores/threadDraftStore';
import { useComposeSheetStore } from '@/core/stores/composeSheetStore';
import type { PostAnalytics } from '@/core/models/post';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface PostMoreMenuProps {
  post: Post;
  /** True when the signed-in user authored this post. */
  isOwn: boolean;
  onReport: () => void;
  /** Own-post only: open the edit dialog. */
  onEdit?: () => void;
  /** Own-post only: open the delete-confirm dialog. */
  onDelete?: () => void;
  /** Other's-post only: called after a successful hide, so the host can remove the card. */
  onHide?: () => void;
  /** Other's-post only: called after the "Undo" toast action successfully restores
   *  the post server-side — the host should clear whatever local hidden state it
   *  set in onHide (postsService.unhidePost already restores the store itself). */
  onUnhide?: () => void;
  /** See PostCardProps.forYouContext's docstring (F1) — "Why am I seeing
   *  this?" only ever renders when this is true, regardless of whether
   *  post.forYouReasons happens to be populated (a stale carry-over from a
   *  prior For-You render of the same shared store record). */
  forYouContext?: boolean;
}

// ─── Styled primitives ────────────────────────────────────────────────────────

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }

  &[aria-disabled='true'] {
    color: ${({ theme }) => theme.color.textMuted};
    cursor: default;
  }
`;

const DangerItem = styled(MenuItem)`
  color: ${({ theme }) => theme.color.danger};
`;

const Divider = styled.div`
  height: 1px;
  margin: 6px 4px;
  background: ${({ theme }) => theme.color.glassBorder};
`;

const ReasonsRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 220px;
  max-width: 280px;
  padding: 4px;
`;

// role="heading" (not a bare div — F5): the Popover panel already carries
// role="menu" from Popover.tsx, and a menu's accessible children are
// menuitem/group/separator — a plain unlabelled div here would be invisible
// to a screen reader browsing in menu-navigation mode. A heading role is
// explicitly compliant structure inside a menu (same pattern browsers use
// for a menu section header) and still gets announced.
const ReasonsHead = styled.div.attrs({ role: 'heading', 'aria-level': 2 })`
  font-family: ${({ theme }) => theme.font.display};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

// role="menuitem" + aria-disabled (F5): each reason is INFORMATIONAL, not
// actionable, but still needs to be a first-class menu child so a screen
// reader in menu-navigation mode (arrow-key browsing) actually reaches and
// announces it — a plain <div> sibling of real menuitem rows is skipped
// entirely by that navigation mode, which is the accessibility bug this
// replaces. aria-disabled (not native disabled, which isn't valid on a div
// anyway) communicates "present but not interactive" the same way the rest
// of this file marks disabled items (see MenuItem's [aria-disabled] style).
const ReasonRow = styled.div.attrs({ role: 'menuitem', 'aria-disabled': true })`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const ReasonIcon = styled.span`
  color: ${({ theme }) => theme.color.textMuted};
  display: flex;
  align-items: center;
  padding-top: 2px;
`;

const REASON_ICON: Record<ForYouReason['kind'], IconName> = {
  follow: 'user-check',
  affinity: 'heart',
  topic: 'hash',
  recency: 'clock',
};

// ─── Component ────────────────────────────────────────────────────────────────

export function PostMoreMenu({
  post,
  isOwn,
  onReport,
  onEdit,
  onDelete,
  onHide,
  onUnhide,
  forYouContext = false,
}: PostMoreMenuProps) {
  const [open, setOpen] = useState(false);
  // Popover family pattern: a single local `view` slot swaps the popover's
  // content between the main menu, the "why am I seeing this" reasons list,
  // and (1.23.00 Phase 7) basic post analytics — rather than nesting a
  // second Popover. Reset whenever the popover closes so it always reopens
  // on the main menu.
  const [view, setView] = useState<'menu' | 'reasons' | 'analytics'>('menu');
  const [analytics, setAnalytics] = useState<PostAnalytics | null | 'loading'>(null);
  const toast = useToast();
  const requireAuth = useRequireAuth();
  const gate = useWriteGate();
  const openComposeSheet = useComposeSheetStore((s) => s.openSheet);
  const { isPremium } = useSession();
  const { isFollowing, isRequested, toggleFollow } = useFollow(post.handle);
  const { isMuted, toggleMute } = useMute(post.handle);
  const deleted = post.deleted === true;
  // F1 + F12: reasons are only ever a real thing to show when this card is
  // actually being rendered in the For-You feed context AND each reason
  // carries a non-empty label — never a bare-icon row for a broken/blank one.
  const reasons = forYouContext ? (post.forYouReasons?.filter((r) => r.label) ?? []) : [];

  // F11: every close path — including the Popover's own backdrop/Escape
  // dismissal via onOpenChange — routes through this ONE helper, so the
  // `view` slot can never be left on 'reasons' after the menu closes no
  // matter which of the many action handlers below fired.
  function closeMenu() {
    setOpen(false);
    setView('menu');
  }

  function handleOpenChange(next: boolean) {
    if (next) setOpen(true);
    else closeMenu();
  }

  function handleShowReasons() {
    setView('reasons');
  }

  function handleShowAnalytics() {
    setView('analytics');
    setAnalytics('loading');
    void postsService.postAnalytics(post.id).then(setAnalytics);
  }

  function handleReport() {
    closeMenu();
    onReport();
  }

  function handleEdit() {
    closeMenu();
    onEdit?.();
  }

  function handleDelete() {
    closeMenu();
    onDelete?.();
  }

  // 1.23.00 Phase 5: own-post schedule/draft management. Free for everyone —
  // not a Pro gate.
  function handlePublishNow() {
    closeMenu();
    void postsService.publishPost(post.id).then((updated) => {
      toast(updated ? 'Published' : 'Could not publish this post');
    });
  }

  function handleUnschedule() {
    closeMenu();
    void postsService.unschedulePost(post.id).then((updated) => {
      toast(updated ? 'Schedule cancelled, kept as a draft' : 'Could not cancel the schedule');
    });
  }

  // 1.24.00 C2: stage this post as the thread root and open a composer. No
  // requireAuth gate — this item only renders on your OWN post, which already
  // means you are signed in.
  function handleAddToThread() {
    closeMenu();
    startThreadPart(post, {
      openComposeSheet,
    });
  }

  function handleFollow() {
    closeMenu();
    if (!requireAuth('follow')) return;
    void toggleFollow()
      .then((r) => {
        if (r.requested) toast(`Follow request sent to ${post.handle}`);
      })
      .catch(() => toast('Something went wrong'));
  }

  function handleCopyLink() {
    closeMenu();
    // 1.27.70: sharing is locked on the Videos pages without two-factor (no-op elsewhere).
    if (!gate.allow('share')) return;
    // shareCode when present, legacy id otherwise — see core/lib/postUrl.ts
    // (shared with the SharePostDialog so the two can never drift apart).
    const url = buildPostShareUrl(post);
    // navigator.clipboard is undefined outside a secure context (plain http, some
    // embedded webviews) — guard rather than let that throw synchronously.
    if (!navigator.clipboard) {
      toast('Could not copy link');
      return;
    }
    void navigator.clipboard.writeText(url).then(
      () => toast('Link copied'),
      () => toast('Could not copy link'),
    );
  }

  function handleNotInterested() {
    closeMenu();
    if (!requireAuth('hide this post')) return;
    void postsService.hidePost(post.id).then((ok) => {
      if (ok) {
        // unhidePost re-toggles the server-side flag off AND restores `post` (which
        // we still have in hand) into the local store — hidePost only removes.
        toast("You won't see this post again", {
          label: 'Undo',
          onClick: () => {
            void postsService.unhidePost(post).then((hidden) => {
              // hidden === false: genuinely unhidden. true (stale re-hide) or
              // null (request failed) are both "did not work" from here.
              if (hidden === false) onUnhide?.();
              else toast('Something went wrong');
            });
          },
        });
        onHide?.();
      } else {
        toast('Something went wrong');
      }
    });
  }

  function handleMute() {
    closeMenu();
    if (!requireAuth('mute this account')) return;
    void toggleMute().then((muted) => {
      if (muted === null) {
        toast('Something went wrong');
        return;
      }
      toast(muted ? `Muted ${post.handle}` : `Unmuted ${post.handle}`);
    });
  }

  return (
    <Popover
      trigger={<IconButton icon="more-horizontal" label="More post actions" size={18} />}
      open={open}
      onOpenChange={handleOpenChange}
    >
      {view === 'analytics' ? (
        <ReasonsRoot role="group" aria-label="Post analytics">
          <ReasonsHead>Post analytics</ReasonsHead>
          {analytics === 'loading' && <ReasonRow>Loading…</ReasonRow>}
          {analytics === null && <ReasonRow>Couldn&apos;t load analytics right now.</ReasonRow>}
          {analytics && analytics !== 'loading' && (
            <>
              <ReasonRow>Impressions (approximate): {analytics.impressions}</ReasonRow>
              <ReasonRow>Cherishes: {analytics.cheers}</ReasonRow>
              <ReasonRow>Spreads: {analytics.spreads}</ReasonRow>
              <ReasonRow>Comments: {analytics.comments}</ReasonRow>
              <ReasonRow>Quotes: {analytics.quotes}</ReasonRow>
            </>
          )}
          <MenuItem type="button" role="menuitem" onClick={() => setView('menu')}>
            <Icon name="chevron-left" size={16} />
            Back
          </MenuItem>
        </ReasonsRoot>
      ) : view === 'reasons' && reasons.length > 0 ? (
        <ReasonsRoot role="group" aria-label="Why am I seeing this post">
          <ReasonsHead>Why am I seeing this?</ReasonsHead>
          {reasons.map((r, i) => (
            <ReasonRow key={i}>
              <ReasonIcon aria-hidden="true">
                <Icon name={REASON_ICON[r.kind] ?? 'info'} size={14} />
              </ReasonIcon>
              <span>{r.label}</span>
            </ReasonRow>
          ))}
          <MenuItem type="button" role="menuitem" onClick={() => setView('menu')}>
            <Icon name="chevron-left" size={16} />
            Back
          </MenuItem>
        </ReasonsRoot>
      ) : isOwn ? (
        <>
          {/* A deleted post has nothing left to edit — hide the item rather than
              open a dialog whose Save is a guaranteed 400 "deleted". */}
          {!deleted && (
            <MenuItem type="button" role="menuitem" onClick={handleEdit}>
              <Icon name="edit" size={16} />
              Edit post
            </MenuItem>
          )}
          {!deleted && post.published === false && (
            <>
              <MenuItem type="button" role="menuitem" onClick={handlePublishNow}>
                <Icon name="send" size={16} />
                Publish now
              </MenuItem>
              {post.scheduledAt && (
                <MenuItem type="button" role="menuitem" onClick={handleUnschedule}>
                  <Icon name="calendar-clock" size={16} />
                  Cancel schedule
                </MenuItem>
              )}
            </>
          )}
          {/* 1.24.00 C2: continue this post as a thread. Published posts only —
              a draft/scheduled post has no thread to hang a part off yet, and
              the server rejects an unpublished root outright. A deleted post
              can't be continued either. */}
          {!deleted && post.published !== false && (
            <MenuItem type="button" role="menuitem" onClick={handleAddToThread}>
              <Icon name="list-ordered" size={16} />
              Add to thread
            </MenuItem>
          )}
          {/* 1.24.00 S2: the same Copy link the other's-post branch has, via the
              same handleCopyLink/buildPostShareUrl — the share popover keeps its
              exact three items, this is the ··· menu's own copy of the link. */}
          <MenuItem type="button" role="menuitem" onClick={handleCopyLink}>
            <Icon name="link" size={16} />
            Copy link to post
          </MenuItem>
          {reasons.length > 0 && (
            <MenuItem type="button" role="menuitem" onClick={handleShowReasons}>
              <Icon name="info" size={16} />
              Why am I seeing this?
            </MenuItem>
          )}
          {/* 1.23.00 Phase 7: Pro-only — hidden entirely for a free author
              rather than shown as a button that would just 403 on click. */}
          {!deleted && post.published !== false && isPremium && (
            <MenuItem type="button" role="menuitem" onClick={handleShowAnalytics}>
              <Icon name="bar-chart-3" size={16} />
              View analytics
            </MenuItem>
          )}
          <DangerItem type="button" role="menuitem" onClick={handleDelete}>
            <Icon name="trash" size={16} />
            Delete post
          </DangerItem>
        </>
      ) : (
        <>
          <MenuItem type="button" role="menuitem" onClick={handleFollow}>
            <Icon name={isFollowing ? 'user-check' : 'user-plus'} size={16} />
            {isFollowing
              ? `Unfollow ${post.handle}`
              : isRequested
                ? `Cancel request to ${post.handle}`
                : `Follow ${post.handle}`}
          </MenuItem>
          <MenuItem type="button" role="menuitem" onClick={handleCopyLink}>
            <Icon name="link" size={16} />
            Copy link to post
          </MenuItem>
          <MenuItem type="button" role="menuitem" onClick={handleNotInterested}>
            <Icon name="eye-off" size={16} />
            Not interested in this post
          </MenuItem>
          {reasons.length > 0 && (
            <MenuItem type="button" role="menuitem" onClick={handleShowReasons}>
              <Icon name="info" size={16} />
              Why am I seeing this?
            </MenuItem>
          )}

          <Divider role="separator" />

          <MenuItem type="button" role="menuitem" onClick={handleMute}>
            <Icon name="volume-x" size={16} />
            {isMuted ? `Unmute ${post.handle}` : `Mute ${post.handle}`}
          </MenuItem>
          <MenuItem type="button" role="menuitem" onClick={handleReport}>
            <Icon name="flag" size={16} />
            Report post
          </MenuItem>
        </>
      )}
    </Popover>
  );
}
